import React, { useRef, useEffect } from 'react';

const TradeForm = ({
  // Item data
  item,
  rowIndex,

  // Form data
  tradeFormData,
  onTradeFormChange,

  // Account and symbol data
  availableAccounts,
  accountMapping,
  securityLevelModelSymbols,
  loadingSymbolsFor,
  symbolHoldings,
  // Cash-related data
  getAccountCashStatus,
  accountCashLevels,
  isLoadingCashLevels,
  formatCurrency,

  // Symbol restrictions
  getSymbolRestriction,

  // Trade management functions
  addTradeRow,
  removeTradeRow,

  // Loading states
  isLoading = false,

  // Add symbolsForModelsAccounts to props
  symbolsForModelsAccounts,
}) => {
  // Track accounts we've already tried to reload to prevent infinite retries
  const retriedAccounts = useRef(new Set());
  const loadingTimeouts = useRef(new Map());
  const circuitBreakerTripped = useRef(false);

  // Effect to cleanup timeouts on unmount
  useEffect(() => {
    const timeouts = loadingTimeouts.current;
    return () => {
      timeouts.forEach(timeout => clearTimeout(timeout));
      timeouts.clear();
    };
  }, []);

  // Effect to implement circuit breaker - stop all retries after too many failures
  useEffect(() => {
    if (retriedAccounts.current.size > 25) {
      circuitBreakerTripped.current = true;
      console.warn('Debug: Circuit breaker activated - stopping all account reload attempts to prevent server overload');

      // Clear all timeouts to stop pending retries
      loadingTimeouts.current.forEach(timeout => clearTimeout(timeout));
      loadingTimeouts.current.clear();
    }
  }, []);

  // Helper function to get cash warning status for a trade
  const getCashWarningStatus = (trade) => {
    if (!trade.account || !trade.symbol || !trade.quantity) {
      return { hasWarning: false, warningType: null, message: null };
    }

    const accountNumber = accountMapping[trade.account];
    if (!accountNumber) return { hasWarning: false, warningType: null, message: null };

    // Get updated cash levels for the account (from trades)
    const updatedCashInfo = accountCashLevels[accountNumber];

    if (!updatedCashInfo) {
      // No trades affecting cash yet, but we can still check current cash status
      // Use getAccountCashStatus to get original data
      const accountCashStatus = getAccountCashStatus(trade.account);
      if (!accountCashStatus) return { hasWarning: false, warningType: null, message: null };

      // For trades without cash impact yet, only check current cash vs reserves
      // Don't show warnings for potential future state - only current state
      return { hasWarning: false, warningType: null, message: null };
    }

    const { new_cash_level, min_reserve, max_reserve } = updatedCashInfo;

    // Check for negative cash balance
    if (new_cash_level < 0) {
      return {
        hasWarning: true,
        warningType: 'negative',
        message: `This trade would result in negative cash balance: ${formatCurrency(new_cash_level)}`,
        severity: 'error'
      };
    }

    // Check for cash below minimum reserve
    if (new_cash_level < min_reserve) {
      return {
        hasWarning: true,
        warningType: 'below-minimum',
        message: `Cash balance ${formatCurrency(new_cash_level)} would be below minimum reserve ${formatCurrency(min_reserve)}`,
        severity: 'warning'
      };
    }

    // Check for cash above maximum reserve (show as informational warning)
    if (new_cash_level > max_reserve && max_reserve > 0) {
      return {
        hasWarning: true, // Treat as a warning for visual purposes
        warningType: 'above-maximum',
        message: `Cash balance ${formatCurrency(new_cash_level)} would be above maximum reserve ${formatCurrency(max_reserve)}`,
        severity: 'info',
        isInformational: true // Flag to indicate this is informational only
      };
    }

    return { hasWarning: false, warningType: null, message: null };
  };

  const getRestrictionWarningStatus = (trade) => {
    // Check if the trade has a restriction message
    if (!trade.restriction || trade.restriction.trim() === '') {
      return { hasWarning: false, message: null };
    }

    if ((trade.restriction.toUpperCase().includes('HOLD') && (trade.quantity !== 0)) ||
      (trade.restriction.toUpperCase().includes('BUY') && (trade.quantity < 0)) ||
      (trade.restriction.toUpperCase().includes('SELL') && (trade.quantity > 0))) {

      return {
        hasWarning: true,
        message: trade.restriction,
        severity: 'warning'
      };
    }

    return { hasWarning: false, message: null };
  };

  // Helper function to render cash warning badge for a trade
  const renderCashWarning = (trade) => {
    const warningStatus = getCashWarningStatus(trade);

    if (!warningStatus.hasWarning) {
      return null;
    }

    const { severity } = warningStatus;

    // Get the same cash data that was used to trigger the warning
    const accountNumber = accountMapping[trade.account];
    const updatedCashInfo = accountCashLevels[accountNumber];

    let badgeClass = 'badge ';
    let iconClass = 'bi ';
    let badgeText = '';
    let detailedMessage = '';

    if (updatedCashInfo) {
      // Use the actual cash data from the warning calculation
      const currentCash = updatedCashInfo.new_cash_level;
      const minReserve = updatedCashInfo.min_reserve;
      const maxReserve = updatedCashInfo.max_reserve;

      switch (severity) {
        case 'error':
          badgeClass += 'bg-danger text-white';
          iconClass += 'bi-exclamation-triangle-fill';
          badgeText = 'Insufficient Cash';
          detailedMessage = `Current: ${formatCurrency(currentCash)} | Range: ${formatCurrency(minReserve)} - ${formatCurrency(maxReserve)}`;
          break;
        case 'warning':
          badgeClass += 'bg-warning text-dark';
          iconClass += 'bi-exclamation-triangle';
          badgeText = `Below Reserve`;
          detailedMessage = `Current: ${formatCurrency(currentCash)} | Range: ${formatCurrency(minReserve)} - ${formatCurrency(maxReserve)}`;
          break;
        case 'info':
          badgeClass += 'bg-info text-white';
          iconClass += 'bi-info-circle';
          badgeText = `Above Reserve`;
          detailedMessage = `Current: ${formatCurrency(currentCash)} | Range: ${formatCurrency(minReserve)} - ${formatCurrency(maxReserve)}`;
          break;
        default:
          return null;
      }
    } else {
      // Fallback to original account status if no updated cash info
      const accountCashStatus = getAccountCashStatus(trade.account);

      switch (severity) {
        case 'error':
          badgeClass += 'bg-danger text-white';
          iconClass += 'bi-exclamation-triangle-fill';
          badgeText = 'Insufficient Cash';
          detailedMessage = `Current: ${formatCurrency(accountCashStatus?.currentCash || 0)} | Range: ${formatCurrency(accountCashStatus?.minReserve || 0)} - ${formatCurrency(accountCashStatus?.maxReserve || 0)} [Account: ${trade.account}]`;
          break;
        case 'warning':
          badgeClass += 'bg-warning text-dark';
          iconClass += 'bi-exclamation-triangle';
          badgeText = 'Below Reserve';
          detailedMessage = `Current: ${formatCurrency(accountCashStatus?.currentCash || 0)} | Range: ${formatCurrency(accountCashStatus?.minReserve || 0)} - ${formatCurrency(accountCashStatus?.maxReserve || 0)} [Account: ${trade.account}]`;
          break;
        case 'info':
          badgeClass += 'bg-info text-white';
          iconClass += 'bi-info-circle';
          badgeText = `Above Reserve: ${formatCurrency(accountCashStatus?.currentCash || 0)} (Range: ${formatCurrency(accountCashStatus?.minReserve || 0)} - ${formatCurrency(accountCashStatus?.maxReserve || 0)})`;
          detailedMessage = `Current: ${formatCurrency(accountCashStatus?.currentCash || 0)} | Range: ${formatCurrency(accountCashStatus?.minReserve || 0)} - ${formatCurrency(accountCashStatus?.maxReserve || 0)} [Account: ${trade.account}]`;
          break;
        default:
          return null;
      }
    }

    return (
      <span
        className={`${badgeClass} ms-2`}
        style={{ fontSize: '0.7rem', padding: '3px 6px' }}
        title={detailedMessage}
      >
        <i className={iconClass} style={{ fontSize: '0.6rem' }}></i>
        <span className="ms-1">{badgeText}</span>
      </span>
    );
  };

  const renderRestrictionWarning = (trade) => {
    const warningStatus = getRestrictionWarningStatus(trade);

    if (!warningStatus.hasWarning) {
      return null;
    }

    const { message, severity } = warningStatus;

    let badgeClass = 'badge ';
    let iconClass = 'bi ';
    let badgeText = 'Trade Restricted';
    let detailedMessage = message;

    switch (severity) {
      case 'error':
        badgeClass += 'bg-danger text-white';
        iconClass += 'bi-exclamation-triangle-fill';
        break;
      case 'warning':
        badgeClass += 'bg-warning text-dark';
        iconClass += 'bi-exclamation-triangle';
        break;
      case 'info':
        badgeClass += 'bg-info text-white';
        iconClass += 'bi-info-circle';
        break;
      default:
        return null;
    }

    return (
      <span
        className={`${badgeClass} ms-2`}
        style={{ fontSize: '0.7rem', padding: '3px 6px' }}
        title={detailedMessage}
      >
        <i className={iconClass} style={{ fontSize: '0.6rem' }}></i>
        <span className="ms-1">{badgeText}</span>
      </span>
    );
  };
  const symbolName = String(item.Symbol || '');
  const formData = tradeFormData[rowIndex] || {
    trades: [{
      symbol: symbolName,
      account: '',
      action: 'Buy',
      quantity: 0,
      price: 0.0,
      dollars: 0,
      currentHoldings: 0,
      restriction: getSymbolRestriction ? getSymbolRestriction(symbolName) : null
    }],
    resultingTarget: item['Target Weight'] || 0
  };

  const modelName = item['Security Level Model Name'];  // Ensure there's always at least one trade to display
  const trades = formData.trades && formData.trades.length > 0 ? formData.trades : [{
    symbol: String(item.Symbol || ''),
    account: '',
    action: 'Buy',
    quantity: 0,
    price: 0.0,
    dollars: 0,
    currentHoldings: 0,
    restriction: getSymbolRestriction ? getSymbolRestriction(String(item.Symbol || '')) : null
  }];

  // Helper function to get symbols for a specific account
  const getSymbolsForAccount = (accountName) => {
    const accountNumber = accountMapping[accountName] || 'all';
    const modelNameKey = String(modelName || '');

    // Primary: Use symbolsForModelsAccounts if available (efficient single API call)
    if (
      symbolsForModelsAccounts &&
      symbolsForModelsAccounts[accountNumber] &&
      symbolsForModelsAccounts[accountNumber][modelNameKey]
    ) {
      const arr = symbolsForModelsAccounts[accountNumber][modelNameKey];
      if (Array.isArray(arr)) {
        return {
          symbols: arr.map(item => (item.Symbol || item.symbol || '')),
          symbolData: arr,
          isLoading: false, // Data is already loaded
          accountHoldings: {} // Not needed when using symbolsForModelsAccounts
        };
      }
    }

    // Fallback: Use old individual call data (less efficient)
    const symbolsKey = `${modelNameKey}|${accountNumber}`;
    const symbols = securityLevelModelSymbols[symbolsKey] || [];
    const symbolData = securityLevelModelSymbols[`${symbolsKey}_data`] || [];
    const isLoading = loadingSymbolsFor[symbolsKey] || false;

    return {
      symbols,
      symbolData,
      isLoading,
      accountHoldings: symbolHoldings[accountNumber] || {}
    };
  };  // Helper function to get current holdings for a specific trade
  const getCurrentHoldings = (trade) => {
    if (!trade.account || !trade.symbol) return 0;

    const accountNumber = accountMapping[trade.account];
    if (!accountNumber) return 0;

    const { symbols, symbolData, accountHoldings } = getSymbolsForAccount(trade.account);

    // Use the same logic as in renderSymbolOptions to get consistent quantity
    const symbolsToUse = symbolData.length > 0 ? symbolData : symbols;
    const symbolItem = symbolsToUse.find(item => {
      const symbol = typeof item === 'string' ? item : (item.Symbol || item.symbol);
      return symbol === trade.symbol;
    });

    if (symbolItem) {
      // Use the same quantity logic as in the dropdown - handle both capital and lowercase
      return typeof symbolItem === 'object' ? (symbolItem.Quantity || symbolItem.quantity || 0) : (accountHoldings[trade.symbol] || 0);
    }

    // Fallback to accountHoldings if symbol not found in symbolsToUse
    return accountHoldings[trade.symbol] || 0;
  };

  // Helper function to determine action label based on quantity comparison
  const getActionLabel = (trade) => {
    const quantity = trade.quantity || 0;

    if (quantity === 0) {
      return { action: 'Hold', class: 'bg-secondary' };
    } else if (quantity > 0) {
      return { action: 'Buy', class: 'bg-success' };
    } else {
      return { action: 'Sell', class: 'bg-danger' };
    }
  };

  // Helper function to render symbol options for a trade
  const renderSymbolOptions = (trade, tradeIndex) => {
    const { symbols, symbolData, isLoading, accountHoldings } = getSymbolsForAccount(trade.account);

    if (!trade.account) {
      return <option value="">Select Account First</option>;
    }

    if (isLoading) {
      return <option value="">Loading symbols...</option>;
    }

    const symbolsToUse = symbolData.length > 0 ? symbolData : symbols;

    return symbolsToUse.map((item, index) => {
      // Handle both enhanced data format and legacy symbols array
      const symbol = typeof item === 'string' ? item : (item.Symbol || item.symbol);
      const quantity = typeof item === 'object' ? (item.Quantity || item.quantity || 0) : (accountHoldings[symbol] || 0);
      const restriction = typeof item === 'object' ? (item.Restriction || item.restriction) : getSymbolRestriction(symbol);

      // Create readable format
      let displayText = symbol;

      // Add quantity info
      if (quantity > 0) {
        displayText += ` (${quantity})`;
      } else {
        displayText += ' (0)';
      }

      // Add restriction info if available
      if (restriction) {
        displayText += ` [${restriction}]`;
      }

      return (
        <option key={symbol} value={symbol}>
          {displayText}
        </option>
      );
    });
  };

  // Helper function to get all accounts with holdings for the current security level model
  const getAccountHoldingsForModel = () => {
    if (!modelName) return { accountHoldings: [], isAnyAccountLoading: false };

    const accountHoldings = [];
    let accountsProcessed = 0;
    let totalAccounts = 0;
    let hasAnyData = false;

    // Iterate through all available accounts and check if they have holdings for symbols in this model
    availableAccounts.forEach(accountName => {
      const accountNumber = accountMapping[accountName];
      if (!accountNumber) {
        return; // Skip accounts without mapping
      }

      totalAccounts++; // Count valid accounts

      // Get symbols for this account and model
      const { symbolData, symbols, isLoading } = getSymbolsForAccount(accountName);

      // If this account is still loading (only applies to fallback method), don't count it as processed
      if (isLoading) {
        return;
      }

      // Use the new data structure when available
      const symbolsToUse = symbolData.length > 0 ? symbolData : symbols;

      // Only count as processed if we have actual data
      if (symbolsToUse.length > 0) {
        accountsProcessed++;
        hasAnyData = true;
      }
      // Build account holdings for display (symbols with non-zero quantities)
      const accountSymbolHoldings = [];
      symbolsToUse.forEach(item => {
        const symbol = typeof item === 'string' ? item : (item.Symbol || item.symbol);
        const quantity = typeof item === 'object' ? (item.Quantity || item.quantity || 0) : 0;
        let price = 0;
        if (typeof item === 'object') {
          price = parseFloat(item.Price || item.price || 0);
        }
        if (quantity > 0) {
          accountSymbolHoldings.push({
            symbol,
            quantity,
            restriction: typeof item === 'object' ? (item.Restriction || item.restriction) : null,
            value: quantity * price
          });
        }
      });

      if (accountSymbolHoldings.length > 0) {
        accountHoldings.push({
          accountName,
          accountNumber,
          holdings: accountSymbolHoldings,
          totalHoldings: accountSymbolHoldings.length
        });
      }
    });

    // Show loading only if using fallback method and not all accounts are processed
    const isAnyAccountLoading = !hasAnyData || (accountsProcessed < totalAccounts && accountsProcessed === 0);

    return {
      accountHoldings,
      isAnyAccountLoading
    };
  };

  // Main render function for the entire trade form
  return (
    <div className="trade-form p-3" style={{ border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>      {/* Account Holdings and Combined Resulting Weight Display */}
      <div className="mb-3">
        <div className="row">
          {/* Account Holdings Panel */}
          <div className="col-md-3">
            <div className="card border bg-light">
              <div className="card-body py-2">
                {(() => {
                  const { accountHoldings, isAnyAccountLoading } = getAccountHoldingsForModel();

                  if (isAnyAccountLoading) {
                    return (
                      <div className="text-center text-muted small py-2">
                        <i className="bi bi-hourglass-split me-1"></i>
                        Loading holdings...
                      </div>
                    );
                  }

                  if (accountHoldings.length === 0) {
                    return (
                      <div className="text-center text-muted small py-2">
                        No accounts with holdings for this model
                      </div>
                    );
                  }

                  return (
                    <div className="small">
                      {accountHoldings.map((account, index) => (
                        <div key={account.accountNumber} className="mb-2">
                          <div className="fw-semibold text-dark mb-1">
                            {account.accountName}
                          </div>
                          <div className="ps-3">
                            {account.holdings.map((holding, holdingIndex) => (
                              <div
                                key={holding.symbol}
                                className="d-flex justify-content-between align-items-center py-1"
                                style={{ borderBottom: holdingIndex < account.holdings.length - 1 ? '1px solid #e9ecef' : 'none' }}
                              >
                                <span className="text-dark">
                                  {holding.symbol} ({holding.quantity.toLocaleString()})
                                </span>
                                <span className="text-muted">
                                  {formatCurrency(holding.value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Combined Resulting Weight Panel */}
          <div className="col-md-9">
            <div className="alert alert-info">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center flex-shrink-0">
                  <i className="bi bi-calculator me-2"></i>
                  <span className="fw-bold">Combined Resulting Weight:</span>
                  {/* Check if any trades have cash warnings */}
                  {(() => {
                    const tradesWithWarnings = trades.map(trade => {
                      const warning = getCashWarningStatus(trade);
                      const accountCashStatus = getAccountCashStatus(trade.account);
                      return { trade, warning, accountCashStatus };
                    }).filter(item => item.warning.hasWarning || item.warning.isInformational);

                    const errorWarnings = tradesWithWarnings.filter(item => item.warning.severity === 'error');
                    const warningItems = tradesWithWarnings.filter(item => item.warning.severity === 'warning');
                    const infoItems = tradesWithWarnings.filter(item => item.warning.severity === 'info');

                    if (errorWarnings.length > 0) {
                      const accountDetails = errorWarnings.map(item =>
                        `Current: ${formatCurrency(item.accountCashStatus?.currentCash || 0)} | Range: ${formatCurrency(item.accountCashStatus?.minReserve || 0)} - ${formatCurrency(item.accountCashStatus?.maxReserve || 0)}`
                      ).join('; ');

                      return (
                        <span className="ms-2 badge bg-danger text-white" style={{ fontSize: '0.7rem' }} title={accountDetails}>
                          <i className="bi bi-exclamation-triangle-fill me-1"></i>
                          {errorWarnings.length} Account{errorWarnings.length > 1 ? 's' : ''}: Insufficient Cash
                        </span>
                      );
                    } else if (warningItems.length > 0) {
                      // Build tooltip with both warning and info items
                      const accountDetails = [
                        ...warningItems.map(item =>
                          `Below Reserve - Current: ${formatCurrency(item.accountCashStatus?.currentCash || 0)} | Range: ${formatCurrency(item.accountCashStatus?.minReserve || 0)} - ${formatCurrency(item.accountCashStatus?.maxReserve || 0)}`
                        ),
                        ...infoItems.map(item =>
                          `Above Reserve - Current: ${formatCurrency(item.accountCashStatus?.currentCash || 0)} | Range: ${formatCurrency(item.accountCashStatus?.minReserve || 0)} - ${formatCurrency(item.accountCashStatus?.maxReserve || 0)}`
                        )
                      ].join('; ');

                      return (
                        <span className="ms-2 badge bg-warning text-dark" style={{ fontSize: '0.7rem' }} title={accountDetails}>
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          {warningItems.length} Account{warningItems.length > 1 ? 's' : ''}: Cash Warnings
                        </span>
                      );
                    } else if (infoItems.length > 0) {
                      // Only show info items if no warnings - just in tooltip
                      const accountDetails = infoItems.map(item =>
                        `Above Reserve - Current: ${formatCurrency(item.accountCashStatus?.currentCash || 0)} | Range: ${formatCurrency(item.accountCashStatus?.minReserve || 0)} - ${formatCurrency(item.accountCashStatus?.maxReserve || 0)}`
                      ).join('; ');

                      return (
                        <span className="ms-2 badge bg-light text-muted" style={{ fontSize: '0.7rem' }} title={accountDetails}>
                          <i className="bi bi-info-circle me-1"></i>
                          {infoItems.length} Account{infoItems.length > 1 ? 's' : ''}: Above Range
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Weight Visualization Bar - takes remaining space */}
                <div className="flex-grow-1" style={{ paddingLeft: '40px', paddingRight: '40px' }}>
                  {(() => {
                    const currentWeight = formData.resultingTarget ? parseFloat(formData.resultingTarget) : 0;
                    const minTarget = item['Minimum Target Weight'] || item['Target Weight'] || 0;
                    const maxTarget = item['Maximum Target Weight'] || item['Target Weight'] || 0;
                    const target = item['Target Weight'] || 0;

                    // Calculate the focused range (10% padding above and below min/max)
                    const range = maxTarget - minTarget;
                    const padding = Math.max(range * 0.1, 1); // At least 1% padding
                    const rangeMin = Math.max(0, minTarget - padding);
                    const rangeMax = Math.min(100, maxTarget + padding);
                    const rangeSize = rangeMax - rangeMin;

                    // Calculate positions within the focused range
                    const minPos = ((minTarget - rangeMin) / rangeSize) * 100;
                    const maxPos = ((maxTarget - rangeMin) / rangeSize) * 100;
                    const targetPos = ((target - rangeMin) / rangeSize) * 100;
                    const currentPos = ((currentWeight - rangeMin) / rangeSize) * 100;

                    // Create gradient colors: red -> green -> red within the focused range
                    const createGradient = () => {
                      const center = (minPos + maxPos) / 2;

                      return `linear-gradient(to right, 
                        #dc3545 0%, 
                        #dc3545 ${minPos}%, 
                        #28a745 ${center}%, 
                        #dc3545 ${maxPos}%, 
                        #dc3545 100%)`;
                    };

                    return (
                      <div className="position-relative">
                        {/* Weight labels */}
                        <div className="d-flex justify-content-between small text-muted mb-1">
                          <span>{rangeMin.toFixed(1)}%</span>
                          <span>{rangeMax.toFixed(1)}%</span>
                        </div>

                        {/* Main bar container */}
                        <div
                          className="position-relative rounded"
                          style={{
                            height: '20px',
                            background: createGradient(),
                            border: '1px solid #dee2e6'
                          }}
                        >
                          {/* Min target marker */}
                          <div
                            className="position-absolute d-flex flex-column align-items-center"
                            style={{
                              left: `${Math.max(0, Math.min(100, minPos))}%`,
                              top: '-18px',
                              transform: 'translateX(-50%)'
                            }}
                          >
                            <small className="text-muted" style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                              Min: {minTarget.toFixed(1)}%
                            </small>
                            <div
                              style={{
                                width: '1px',
                                height: '20px',
                                backgroundColor: '#495057',
                                opacity: 0.7
                              }}
                            ></div>
                          </div>

                          {/* Max target marker */}
                          <div
                            className="position-absolute d-flex flex-column align-items-center"
                            style={{
                              left: `${Math.max(0, Math.min(100, maxPos))}%`,
                              top: '-18px',
                              transform: 'translateX(-50%)'
                            }}
                          >
                            <small className="text-muted" style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                              Max: {maxTarget.toFixed(1)}%
                            </small>
                            <div
                              style={{
                                width: '1px',
                                height: '20px',
                                backgroundColor: '#495057',
                                opacity: 0.7
                              }}
                            ></div>
                          </div>

                          {/* Target weight marker */}
                          <div
                            className="position-absolute d-flex flex-column align-items-center"
                            style={{
                              left: `${Math.max(0, Math.min(100, targetPos))}%`,
                              top: '-18px',
                              transform: 'translateX(-50%)'
                            }}
                          >
                            <small className="text-primary fw-bold" style={{ fontSize: '0.65rem', whiteSpace: 'nowrap' }}>
                              Target: {target.toFixed(1)}%
                            </small>
                            <div
                              style={{
                                width: '2px',
                                height: '20px',
                                backgroundColor: '#007bff',
                                opacity: 0.9
                              }}
                            ></div>
                          </div>

                          {/* Current weight indicator */}
                          <div
                            className="position-absolute d-flex flex-column align-items-center"
                            style={{
                              left: `${Math.max(0, Math.min(100, currentPos))}%`,
                              top: '50%',
                              transform: 'translate(-50%, -50%)',
                              zIndex: 10
                            }}
                          >
                            <div
                              className="bg-dark text-white rounded px-1 py-0 shadow-sm"
                              style={{
                                fontSize: '0.7rem',
                                whiteSpace: 'nowrap',
                                fontWeight: 'bold'
                              }}
                            >
                              {currentWeight.toFixed(2)}%
                            </div>
                          </div>
                        </div>

                        {/* Bottom padding for labels */}
                        <div style={{ height: '18px' }}></div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trade Configuration Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Trade Configuration</h6>
        <button
          type="button"
          className="btn btn-sm btn-success"
          onClick={() => addTradeRow(rowIndex)}
          title="Add another trade for this security"
        >
          <i className="bi bi-plus-circle me-1"></i>
          Add Trade
        </button>
      </div>

      {/* Column Headers */}
      <div className="row align-items-center mb-1 px-1">
        <div className="col-md-1" style={{ flex: '0 0 6%', maxWidth: '6%' }}>
          <small className="text-muted fw-bold">Trade</small>
        </div>
        <div className="col-md-3">
          <small className="text-muted fw-bold">Account</small>
        </div>
        <div className="col-md-2">
          <small className="text-muted fw-bold">Symbol</small>
        </div>
        <div className="col-md-2 text-end">
          <small className="text-muted fw-bold">Quantity (+Buy/-Sell)</small>
        </div>
        <div className="col-md-2 text-end">
          <small className="text-muted fw-bold">Dollars (+Buy/-Sell)</small>
        </div>
        <div className="col-md-1 text-end" style={{ flex: '0 0 6%', maxWidth: '6%' }}>
          <small className="text-muted fw-bold">Price</small>
        </div>
        <div className="col-md-1 text-center" style={{ flex: '0 0 6%', maxWidth: '6%' }}>
          <small className="text-muted fw-bold">Action</small>
        </div>
        <div className="col-md-1 text-center" style={{ flex: '0 0 6%', maxWidth: '6%' }}>
          <small className="text-muted fw-bold">Delete</small>
        </div>
      </div>

      {/* Trade Rows */}
      {trades.map((trade, tradeIndex) => {
        const actionInfo = getActionLabel(trade);
        const warningStatus = getCashWarningStatus(trade);

        // Determine border color based on cash warning severity
        let borderStyle = '1px solid #ccc';
        if (warningStatus.hasWarning) {
          switch (warningStatus.severity) {
            case 'error':
              borderStyle = '2px solid #dc3545'; // Red for errors
              break;
            case 'warning':
              borderStyle = '2px solid #ffc107'; // Yellow for warnings
              break;
            default:
              borderStyle = '1px solid #ccc'; // Default border
              break;
          }
        }

        return (
          <div key={tradeIndex} className="trade-row mb-1 p-1" style={{ border: borderStyle, borderRadius: '6px', backgroundColor: 'white' }}>
            {/* Single row with all trade fields */}
            <div className="row align-items-center">
              {/* Trade # and Warnings */}
                <div className="col-md-1" style={{ flex: '0 0 6%', maxWidth: '6%' }}> 
                <h6 className="mb-0">{tradeIndex + 1}</h6>
                <div className="d-flex flex-column gap-1">
                  {renderRestrictionWarning(trade)}
                  {renderCashWarning(trade)}
                </div>
              </div>
              {/* Account */}
              <div className="col-md-3">
                <select
                  className="form-select form-select-sm"
                  value={String(trade.account || '')}
                  onChange={(e) => {
                    if (typeof onTradeFormChange === 'function') {
                      onTradeFormChange(rowIndex, 'account', e.target.value, tradeIndex);
                    }
                  }}
                >
                  <option value="">Select Account</option>
                  {availableAccounts.map(account => (
                    <option key={account} value={account}>{account}</option>
                  ))}
                </select>
              </div>
              {/* Symbol */}
              <div className="col-md-2">
                <select
                  className="form-select form-select-sm"
                  value={String(trade.symbol || '')} onChange={(e) => {
                    if (typeof onTradeFormChange === 'function') {
                      // Find the restriction and price for the selected symbol from the symbol data
                      const { symbolData, symbols } = getSymbolsForAccount(trade.account);
                      const symbolsToUse = symbolData.length > 0 ? symbolData : symbols;
                      const selectedSymbol = e.target.value;

                      // Find the symbol data to get its restriction and price
                      const symbolItem = symbolsToUse.find(item => {
                        const symbol = typeof item === 'string' ? item : (item.Symbol || item.symbol);
                        return symbol === selectedSymbol;
                      });

                      const restriction = typeof symbolItem === 'object' ? (symbolItem.Restriction || symbolItem.restriction) : null;
                      const price = typeof symbolItem === 'object' ? (symbolItem.Price || symbolItem.price || 0) : 0;

                      // Pass symbol change with additional data including price
                      onTradeFormChange(rowIndex, 'symbol', selectedSymbol, tradeIndex, { restriction, price });
                    }
                  }}
                  disabled={isLoading || !trade.account}
                >
                  <option value="">
                    {!trade.account ? 'Account First' :
                      isLoading ? 'Loading...' : 'Select Symbol'}
                  </option>
                  {trade.account && renderSymbolOptions(trade, tradeIndex)}
                </select>
              </div>
              {/* Quantity */}
              <div className="col-md-2">
                <div className="d-flex align-items-center">
                  <input
                    type="number"
                    className="form-control form-control-sm text-end"
                    style={{ marginRight: '5px', minWidth: '80px' }}
                    value={(() => {
                      const qty = trade.quantity || 0;
                      return qty % 1 === 0 ? qty.toString() : qty.toFixed(2);
                    })()}
                    onChange={(e) => {
                      const quantity = parseFloat(e.target.value) || 0;

                      // Get current holdings for this symbol and account
                      const currentHoldings = getCurrentHoldings(trade);

                      // Validate that we cannot sell more than we hold
                      // (negative quantity means selling)
                      let validatedQuantity = quantity;
                      if (quantity < 0 && Math.abs(quantity) > currentHoldings) {
                        // If trying to sell more than we hold, limit to what we have
                        validatedQuantity = currentHoldings > 0 ? -currentHoldings : 0;
                      }

                      // Calculate dollars value and update both quantity and dollars
                      const price = trade.price || 0;
                      const dollarValue = validatedQuantity * price;

                      // Always update the quantity and recalculate, even if it would cause warnings
                      // The validation warnings will be shown in the UI but calculation should still happen
                      if (typeof onTradeFormChange === 'function') {
                        onTradeFormChange(rowIndex, 'quantity', validatedQuantity, tradeIndex);
                        onTradeFormChange(rowIndex, 'dollars', dollarValue, tradeIndex);
                      }
                    }}
                    step="1"
                    disabled={!trade.account || !trade.symbol}
                    placeholder="Qty"
                    title={`Current holdings: ${getCurrentHoldings(trade)} shares`}
                  />
                  {/* Quantity adjustment buttons */}
                  <div className="d-flex align-items-center">
                    <div className="btn-group-vertical" role="group">
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '25px' }}
                          disabled={!trade.account || !trade.symbol} onClick={() => {
                            const currentQuantity = trade.quantity || 0;
                            const newQuantity = currentQuantity + 10;
                            const price = trade.price || 0;
                            const dollarValue = newQuantity * price;
                            if (typeof onTradeFormChange === 'function') {
                              onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                              onTradeFormChange(rowIndex, 'dollars', dollarValue, tradeIndex);
                            }
                          }}
                          title="Add 10 shares"
                        >
                          +10
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '30px' }}
                          disabled={!trade.account || !trade.symbol} onClick={() => {
                            const currentQuantity = trade.quantity || 0;
                            const newQuantity = currentQuantity + 100;
                            const price = trade.price || 0;
                            const dollarValue = newQuantity * price;
                            if (typeof onTradeFormChange === 'function') {
                              onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                              onTradeFormChange(rowIndex, 'dollars', dollarValue, tradeIndex);
                            }
                          }}
                          title="Add 100 shares"
                        >
                          +100
                        </button>
                      </div>
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '25px' }}
                          disabled={!trade.account || !trade.symbol} onClick={() => {
                            const currentQuantity = trade.quantity || 0;
                            const currentHoldings = getCurrentHoldings(trade);
                            let newQuantity = currentQuantity - 10;

                            // Validate that we cannot sell more than we hold
                            if (newQuantity < 0 && Math.abs(newQuantity) > currentHoldings) {
                              newQuantity = currentHoldings > 0 ? -currentHoldings : 0;
                            }

                            const price = trade.price || 0;
                            const dollarValue = newQuantity * price;

                            if (typeof onTradeFormChange === 'function') {
                              onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                              onTradeFormChange(rowIndex, 'dollars', dollarValue, tradeIndex);
                            }
                          }}
                          title="Remove 10 shares"
                        >
                          -10
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '30px' }}
                          disabled={!trade.account || !trade.symbol} onClick={() => {
                            const currentQuantity = trade.quantity || 0;
                            const currentHoldings = getCurrentHoldings(trade);
                            let newQuantity = currentQuantity - 100;

                            // Validate that we cannot sell more than we hold
                            if (newQuantity < 0 && Math.abs(newQuantity) > currentHoldings) {
                              newQuantity = currentHoldings > 0 ? -currentHoldings : 0;
                            }

                            const price = trade.price || 0;
                            const dollarValue = newQuantity * price;

                            if (typeof onTradeFormChange === 'function') {
                              onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                              onTradeFormChange(rowIndex, 'dollars', dollarValue, tradeIndex);
                            }
                          }}
                          title="Remove 100 shares"
                        >
                          -100
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      style={{
                        fontSize: '0.6rem',
                        padding: '8px 4px',
                        minWidth: '35px',
                        height: '100%',
                        marginLeft: '2px'
                      }}
                      disabled={!trade.account || !trade.symbol}
                      onClick={() => {
                        const currentQuantity = trade.quantity || 0;
                        const roundedQuantity = Math.round(currentQuantity);
                        const price = trade.price || 0;
                        const dollarValue = roundedQuantity * price;

                        if (typeof onTradeFormChange === 'function') {
                          onTradeFormChange(rowIndex, 'quantity', roundedQuantity, tradeIndex);
                          onTradeFormChange(rowIndex, 'dollars', dollarValue, tradeIndex);
                        }
                      }}
                      title="Round quantity to nearest whole number"
                    >
                      Round
                    </button>
                  </div>
                </div>
              </div>

              {/* Dollars */}
              <div className="col-md-2">
                <div className="d-flex align-items-center">
                  <input
                    type="number"
                    className="form-control form-control-sm text-end"
                    style={{ marginRight: '5px', minWidth: '80px' }}
                    value={(trade.dollars || 0).toFixed(2)}
                    onChange={(e) => {
                      const dollars = parseFloat(e.target.value) || 0;
                      const price = trade.price || 0;

                      // Calculate quantity from dollars/price
                      let newQuantity = 0;
                      if (price > 0) {
                        newQuantity = dollars / price;
                      }

                      // Get current holdings for this symbol and account
                      const currentHoldings = getCurrentHoldings(trade);

                      // Validate that we cannot sell more than we hold
                      // (negative quantity means selling)
                      if (newQuantity < 0 && Math.abs(newQuantity) > currentHoldings) {
                        // If trying to sell more than we hold, limit to what we have
                        newQuantity = currentHoldings > 0 ? -currentHoldings : 0;
                      }

                      // Update both dollars and quantity
                      if (typeof onTradeFormChange === 'function') {
                        onTradeFormChange(rowIndex, 'dollars', dollars, tradeIndex);
                        onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                      }
                    }}
                    step="1"
                    disabled={!trade.account || !trade.symbol}
                    placeholder="$0.00"
                    title={`Dollar value of the trade`}
                  />
                  {/* Dollar adjustment buttons */}
                  <div className="btn-group-vertical" role="group">
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '30px' }}
                        disabled={!trade.account || !trade.symbol}
                        onClick={() => {
                          const currentDollars = trade.dollars || 0;
                          const newDollars = currentDollars + 100;
                          const price = trade.price || 0;
                          let newQuantity = 0;
                          if (price > 0) {
                            newQuantity = newDollars / price;
                          }
                          if (typeof onTradeFormChange === 'function') {
                            onTradeFormChange(rowIndex, 'dollars', newDollars, tradeIndex);
                            onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                          }
                        }}
                        title="Add $100"
                      >
                        +$100
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '35px' }}
                        disabled={!trade.account || !trade.symbol}
                        onClick={() => {
                          const currentDollars = trade.dollars || 0;
                          const newDollars = currentDollars + 1000;
                          const price = trade.price || 0;
                          let newQuantity = 0;
                          if (price > 0) {
                            newQuantity = newDollars / price;
                          }
                          if (typeof onTradeFormChange === 'function') {
                            onTradeFormChange(rowIndex, 'dollars', newDollars, tradeIndex);
                            onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                          }
                        }}
                        title="Add $1000"
                      >
                        +$1K
                      </button>
                    </div>
                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '30px' }}
                        disabled={!trade.account || !trade.symbol} onClick={() => {
                          const currentDollars = trade.dollars || 0;
                          const newDollars = currentDollars - 100;
                          const price = trade.price || 0;
                          let newQuantity = 0;
                          if (price > 0) {
                            newQuantity = newDollars / price;
                          }
                          if (typeof onTradeFormChange === 'function') {
                            onTradeFormChange(rowIndex, 'dollars', newDollars, tradeIndex);
                            onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                          }
                        }}
                        title="Remove $100"
                      >
                        -$100
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        style={{ fontSize: '0.6rem', padding: '1px 2px', minWidth: '35px' }}
                        disabled={!trade.account || !trade.symbol} onClick={() => {
                          const currentDollars = trade.dollars || 0;
                          const newDollars = currentDollars - 1000;
                          const price = trade.price || 0;
                          let newQuantity = 0;
                          if (price > 0) {
                            newQuantity = newDollars / price;
                          }
                          if (typeof onTradeFormChange === 'function') {
                            onTradeFormChange(rowIndex, 'dollars', newDollars, tradeIndex);
                            onTradeFormChange(rowIndex, 'quantity', newQuantity, tradeIndex);
                          }
                        }}
                        title="Remove $1000"
                      >
                        -$1K
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Price */}
              <div className="col-md-1" style={{ flex: '0 0 6%', maxWidth: '6%' }}>
                <div className="text-end small">
                  {trade.price && trade.price > 0 ? `$${trade.price.toFixed(2)}` : '-'}
                </div>
              </div>

              {/* Action */}
              <div className="col-md-1" style={{ flex: '0 0 6%', maxWidth: '6%' }}>
                <div className="text-center">
                  <span className={`badge ${actionInfo.class}`} style={{ fontSize: '0.85rem', padding: '2px 6px' }}>
                    {actionInfo.action}
                  </span>
                </div>
              </div>
              {/* Delete Button */}
              <div className="col-md-1 text-center" style={{ flex: '0 0 6%', maxWidth: '6%' }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => removeTradeRow(rowIndex, tradeIndex)}
                  title={trades.length > 1 ? "Remove this trade" : "Clear trade values"}
                  style={{ fontSize: '0.7rem', padding: '1px 4px' }}
                >
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TradeForm;
